import { useMemo } from 'react';
import { fmtAUD } from '@/shared/utils/formatCurrency';

const MS_DAY = 86400000;
const MS_30 = 30 * MS_DAY;
const MS_60 = 60 * MS_DAY;
const MS_90 = 90 * MS_DAY;

function sumByGroup(txns, getGroup) {
  const acc = {};
  for (const t of txns) {
    const g = getGroup(t.category) || t.category || 'Other';
    acc[g] = (acc[g] || 0) + (Number(t.amount) || 0);
  }
  return acc;
}

export function useSpendingStreaks(allTxns, getGroup, incomeList) {
  return useMemo(() => {
    const now = Date.now();

    const expenseTxns90 = (allTxns || []).filter(t => {
      if (!t || !t.date) return false;
      if (incomeList && incomeList.includes(t.category)) return false;
      if (t.type === 'income') return false;
      const ms = new Date(t.date).getTime();
      return ms >= now - MS_90 && ms <= now;
    });

    if (expenseTxns90.length < 5) return { trendInsights: [], streakInsights: [] };

    // Split into current-30 and prior-30-to-90
    const curr30 = expenseTxns90.filter(t => new Date(t.date).getTime() >= now - MS_30);
    const prev60 = expenseTxns90.filter(t => new Date(t.date).getTime() < now - MS_30);

    const currByGroup = sumByGroup(curr30, getGroup);
    const prevByGroup = sumByGroup(prev60, getGroup);

    // Top 3 groups by current 30d spend, with meaningful prior baseline
    const trendInsights = Object.entries(currByGroup)
      .filter(([, curr]) => curr > 10)
      .sort((a, b) => b[1] - a[1])
      .slice(0, 3)
      .flatMap(([group, curr]) => {
        const prevNorm = (prevByGroup[group] || 0) / 2; // normalize 60d → per-30d
        if (prevNorm < 20) return []; // not enough prior data to compare
        const delta = ((curr - prevNorm) / prevNorm) * 100;
        if (Math.abs(delta) < 15) return []; // not significant
        const up = delta > 0;
        return [{
          icon: up ? '📈' : '📉',
          color: up ? '#ef4444' : '#16a34a',
          text: String(group) + ' is ' + (up ? 'up' : 'down') + ' ' +
            Math.abs(delta).toFixed(0) + '% vs your 3-month average ($' +
            fmtAUD(prevNorm) + '/mo avg, $' + fmtAUD(curr) + ' this month)',
        }];
      });

    // Days-since-last-txn per category (leaf level), for categories with >$50 cumulative in 90d
    const spendByCat = {};
    const lastMsByCat = {};
    for (const t of expenseTxns90) {
      const cat = t.category || '';
      const ms = new Date(t.date).getTime();
      spendByCat[cat] = (spendByCat[cat] || 0) + (Number(t.amount) || 0);
      if (!lastMsByCat[cat] || ms > lastMsByCat[cat]) lastMsByCat[cat] = ms;
    }

    const streakInsights = Object.entries(lastMsByCat)
      .filter(([cat]) => (spendByCat[cat] || 0) >= 50) // only meaningful categories
      .map(([cat, lastMs]) => ({ cat, days: Math.floor((now - lastMs) / MS_DAY) }))
      .filter(s => s.days >= 7 && s.days < 90)
      .sort((a, b) => b.days - a.days)
      .slice(0, 2)
      .map(({ cat, days }) => ({
        icon: '🔥',
        color: '#7c3aed',
        text: days + ' days since your last ' + String(cat) + ' transaction — keep it up!',
      }));

    return { trendInsights, streakInsights };
  }, [allTxns, getGroup, incomeList]);
}
